import React from "react";
import PropTypes from "prop-types";
import { StaticQuery, graphql, Link } from "gatsby";
import Img from "gatsby-image";
import SEO from "../components/seo";
import { listOfTestimonials } from "../data/listOfTestimonials";

const noOfPreviewTestimonials = 2;

const fullBleed = {
	marginLeft: "calc(50% - 50vw)",
	marginRight: "calc(50% - 50vw)",
	paddingLeft: "calc(50vw - 50%)",
	paddingRight: "calc(50vw - 50%)",
};

const WaveDivider = ({ fill, flip }) => (
	<div className="wave-divider" style={flip ? { transform: "rotate(180deg)" } : undefined}>
		<svg viewBox="0 0 1440 90" preserveAspectRatio="none">
			<path d="M0,40 C240,90 480,0 720,30 C960,60 1200,10 1440,45 L1440,90 L0,90 Z" fill={fill} />
		</svg>
	</div>
);

WaveDivider.propTypes = {
	fill: PropTypes.string.isRequired,
	flip: PropTypes.bool,
};

WaveDivider.defaultProps = {
	flip: false,
};

const eyebrow = {
	fontFamily: "var(--body-font)",
	fontSize: 13,
	fontWeight: 600,
	letterSpacing: "0.08em",
	color: "var(--warm-dark)",
	textTransform: "uppercase",
	marginBottom: 18,
};

const sectionHeading = {
	fontFamily: "var(--heading-font)",
	fontWeight: 800,
	fontSize: 32,
	lineHeight: 1.25,
	color: "var(--text)",
	margin: "0 0 16px",
};

const bodyText = {
	fontFamily: "var(--body-font)",
	fontSize: 16.5,
	lineHeight: 1.75,
	color: "var(--text-muted)",
	margin: "0 0 20px",
};

const btnStyle = {
	fontFamily: "var(--body-font)",
	fontSize: 15,
	fontWeight: 600,
	padding: "13px 26px",
	borderRadius: 8,
	textDecoration: "none",
	display: "inline-block",
};

const GlanceItem = ({ bg, icon, text }) => (
	<div style={{ display: "flex", alignItems: "center", gap: 14 }}>
		<div
			style={{
				width: 48,
				height: 48,
				borderRadius: "50%",
				background: bg,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				flexShrink: 0,
			}}
		>
			{icon}
		</div>
		<span style={{ fontFamily: "var(--body-font)", fontSize: 14.5, fontWeight: 500, color: "var(--text)" }}>{text}</span>
	</div>
);

GlanceItem.propTypes = {
	bg: PropTypes.string.isRequired,
	icon: PropTypes.node.isRequired,
	text: PropTypes.string.isRequired,
};

const ServiceCard = ({ bg, icon, title, text }) => (
	<div
		className="card-hover"
		style={{
			background: "var(--surface)",
			border: "1px solid var(--border)",
			borderRadius: 14,
			padding: "32px 26px",
			display: "flex",
			flexDirection: "column",
			gap: 14,
		}}
	>
		<div style={{ width: 56, height: 56, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
			{icon}
		</div>
		<h3 style={{ fontFamily: "var(--heading-font)", fontWeight: 700, fontSize: 17, color: "var(--text)", margin: 0 }}>{title}</h3>
		<p style={{ fontFamily: "var(--body-font)", fontSize: 14, lineHeight: 1.6, color: "var(--text-muted)", margin: 0 }}>{text}</p>
		<Link to="/services" style={{ fontFamily: "var(--body-font)", fontSize: 14, fontWeight: 600, color: "var(--primary-dark)", marginTop: "auto", textDecoration: "none" }}>
			Learn more &rarr;
		</Link>
	</div>
);

ServiceCard.propTypes = {
	bg: PropTypes.string.isRequired,
	icon: PropTypes.node.isRequired,
	title: PropTypes.string.isRequired,
	text: PropTypes.string.isRequired,
};

function IndexPage() {
	return (
		<StaticQuery
			query={graphql`
				query {
					heroConsult: file(relativePath: { eq: "hero-consult.jpg" }) {
						childImageSharp {
							fluid(maxWidth: 900, quality: 85) {
								...GatsbyImageSharpFluid_withWebp
							}
						}
					}
					aboutTeam: file(relativePath: { eq: "about-team.jpg" }) {
						childImageSharp {
							fluid(maxWidth: 1000, quality: 85) {
								...GatsbyImageSharpFluid_withWebp
							}
						}
					}
					gastroCard: file(relativePath: { eq: "gastro-card.jpg" }) {
						childImageSharp {
							fluid(maxWidth: 800, quality: 85) {
								...GatsbyImageSharpFluid_withWebp
							}
						}
					}
					rheumCard: file(relativePath: { eq: "rheum-card.jpg" }) {
						childImageSharp {
							fluid(maxWidth: 800, quality: 85) {
								...GatsbyImageSharpFluid_withWebp
							}
						}
					}
				}
			`}
			render={({ heroConsult, aboutTeam, gastroCard, rheumCard }) => (
				<div>
					<SEO
						title="Gastroenterology, Hepatology & Rheumatology Specialists"
						keywords={[
							`Gastroenterologist`,
							`Rheumatologist`,
							`Paediatric`,
							`A/Prof Spiro Raftopoulos`,
							`Prof Ian Yusoff`,
							`Dr Marcelo Leal`,
							`Dr Daniel Cheng`,
							`Dr Lorenzo Tarquinio`,
							`Dr Sam Rao`,
							`Dr Claire Gordon`,
							`Dr Duy Pham`,
							`Dr Angel Li`,
							`Dr Danielle Lam`,
							`Dr Katrina Rogers`,
							`Dr Muna Salama`,
							`Dr Kelly Morgan`,
						]}
						description="Advanced GI WA is a progressive Perth practice offering leading-edge gastroenterology, hepatology and rheumatology care."
					/>

					{/* Hero */}
					<section style={{ ...fullBleed, position: "relative", overflow: "hidden", background: "var(--bg)" }}>
						<div
							className="hero-row section-pad"
							style={{
								position: "relative",
								zIndex: 1,
								maxWidth: 1344,
								margin: "0 auto",
								padding: "88px 48px 0",
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: 56,
							}}
						>
							<div className="hero-col" style={{ maxWidth: 540 }}>
								<div style={eyebrow}>Gastroenterology &middot; Hepatology &middot; Rheumatology</div>
								<h1 className="hero-h1" style={{ fontFamily: "var(--heading-font)", fontWeight: 800, fontSize: 46, lineHeight: 1.15, color: "var(--text)", margin: "0 0 22px" }}>
									Warm, expert care for your digestive and joint health
								</h1>
								<p style={{ fontFamily: "var(--body-font)", fontSize: 17, lineHeight: 1.65, color: "var(--text-muted)", margin: "0 0 34px", maxWidth: 480 }}>
									Advanced GI WA is a progressive Perth practice offering leading-edge gastroenterology, hepatology and
									rheumatology care &mdash; combining specialist expertise with a comprehensive range of diagnostic and
									therapeutic services.
								</p>
								<div className="cta-row" style={{ display: "flex", gap: 16, alignItems: "center" }}>
									<Link to="/services" className="btn-primary" style={btnStyle}>
										Our Services
									</Link>
									<Link to="/contact" className="btn-warm" style={btnStyle}>
										Contact Us
									</Link>
								</div>
							</div>
							<div className="hero-col" style={{ maxWidth: 460, width: "100%", position: "relative" }}>
								<div style={{ position: "absolute", top: -24, right: -24, width: "88%", height: "92%", borderRadius: 16, background: "var(--tint-warm)", zIndex: 0 }} />
								<div style={{ position: "relative", zIndex: 1, borderRadius: 16, overflow: "hidden", boxShadow: "0 20px 40px rgba(61,72,82,0.16)" }}>
									<Img fluid={heroConsult.childImageSharp.fluid} alt="Specialist consulting warmly with a patient" />
								</div>
							</div>
						</div>
						<div style={{ marginTop: 64 }}>
							<WaveDivider fill="#EAF3F8" />
						</div>
					</section>

					{/* At a glance strip */}
					<section className="section-pad" style={{ ...fullBleed, background: "var(--tint-blue)", padding: "8px 48px 56px" }}>
						<div className="glance-row" style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", gap: 32 }}>
							<GlanceItem
								bg="var(--primary)"
								text="Three specialties: Gastroenterology, Hepatology & Rheumatology"
								icon={
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8">
										<path d="M9 3v3M15 3v3M4 8h16M6 8v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8" />
									</svg>
								}
							/>
							<GlanceItem
								bg="var(--warm)"
								text="Based at Hollywood Private Hospital, Nedlands"
								icon={
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8">
										<path d="M12 21s-7-5.33-7-11a7 7 0 0 1 14 0c0 5.67-7 11-7 11z" />
										<circle cx="12" cy="10" r="2.4" />
									</svg>
								}
							/>
							<GlanceItem
								bg="var(--primary-dark)"
								text="Multidisciplinary specialist team"
								icon={
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8">
										<circle cx="9" cy="7" r="3.2" />
										<circle cx="17" cy="7" r="2.4" />
										<path d="M2.5 21v-1.5A5.5 5.5 0 0 1 8 14h2a5.5 5.5 0 0 1 5.4 4.5" />
										<path d="M16 14.2a4.2 4.2 0 0 1 5.5 4V21" />
									</svg>
								}
							/>
						</div>
					</section>

					{/* About */}
					<section className="section-pad" style={{ ...fullBleed, background: "var(--surface)", padding: "96px 48px" }}>
						<div className="about-row" style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 64 }}>
							<div className="about-col" style={{ maxWidth: 460, width: "100%", flexShrink: 0, position: "relative" }}>
								<div style={{ position: "absolute", bottom: -20, left: -20, width: "85%", height: "85%", borderRadius: 16, background: "var(--tint-blue)", zIndex: 0 }} />
								<div style={{ position: "relative", zIndex: 1, borderRadius: 16, overflow: "hidden", boxShadow: "0 20px 40px rgba(61,72,82,0.14)" }}>
									<Img fluid={aboutTeam.childImageSharp.fluid} alt="Our specialist team" />
								</div>
							</div>
							<div className="about-body" style={{ maxWidth: 600 }}>
								<div style={{ ...eyebrow, marginBottom: 14 }}>About Us</div>
								<h2 style={sectionHeading}>A comprehensive, patient-first practice</h2>
								<p style={bodyText}>
									Advanced GI WA is a progressive gastroenterology and rheumatology practice based in Perth, Western
									Australia. We offer our patients leading-edge treatments and a comprehensive service that covers all
									aspects of luminal gastroenterology and hepatology, together with rheumatology care.
								</p>
								<p style={bodyText}>
									We are a one-stop destination for consultation, diagnostic testing and therapeutic procedures across
									gastroenterology, hepatology and rheumatology. Our specialist team has extensive knowledge and
									experience providing the highest level of medical care to all our patients. Several of our
									specialists hold tertiary hospital affiliations, providing access to clinical trials,
									multi-disciplinary meetings and the latest developments in diagnostics and medical therapy.
								</p>
								<p style={{ ...bodyText, marginBottom: 0 }}>
									All endoscopic procedures are performed at Hollywood Private Hospital, where we have access to the
									most current technologies and up-to-date equipment to diagnose and treat complicated digestive
									disorders.
								</p>
							</div>
						</div>
					</section>

					<div style={fullBleed}>
						<WaveDivider fill="#FBECDF" flip />
					</div>

					{/* Specialty spotlights */}
					<section className="section-pad" style={{ ...fullBleed, background: "var(--tint-warm)", padding: "8px 48px 88px" }}>
						<div className="services-grid" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
							<div style={{ background: "var(--surface)", borderRadius: 16, overflow: "hidden", border: "1px solid var(--border)" }}>
								<Img fluid={gastroCard.childImageSharp.fluid} alt="Specialist discussing digestive anatomy with a patient" style={{ height: 240 }} imgStyle={{ objectFit: "cover" }} />
								<div style={{ padding: "26px 28px" }}>
									<h3 style={{ fontFamily: "var(--heading-font)", fontWeight: 700, fontSize: 19, color: "var(--text)", margin: "0 0 10px" }}>
										Gastroenterology &amp; Hepatology
									</h3>
									<p style={{ fontFamily: "var(--body-font)", fontSize: 14.5, lineHeight: 1.6, color: "var(--text-muted)", margin: 0 }}>
										Consultation, diagnosis and treatment across the full range of digestive and liver conditions,
										including all aspects of luminal gastroenterology and hepatology.
									</p>
								</div>
							</div>
							<div style={{ background: "var(--surface)", borderRadius: 16, overflow: "hidden", border: "1px solid var(--border)" }}>
								<Img fluid={rheumCard.childImageSharp.fluid} alt="Specialist examining a patient's wrist during a consultation" style={{ height: 240 }} imgStyle={{ objectFit: "cover" }} />
								<div style={{ padding: "26px 28px" }}>
									<h3 style={{ fontFamily: "var(--heading-font)", fontWeight: 700, fontSize: 19, color: "var(--text)", margin: "0 0 10px" }}>Rheumatology</h3>
									<p style={{ fontFamily: "var(--body-font)", fontSize: 14.5, lineHeight: 1.6, color: "var(--text-muted)", margin: 0 }}>
										Consultation, diagnosis and ongoing management of routine rheumatological conditions, now
										available as part of our specialist team.
									</p>
								</div>
							</div>
						</div>
					</section>

					{/* Services */}
					<section className="section-pad" style={{ ...fullBleed, background: "var(--tint-warm)", padding: "8px 48px 100px" }}>
						<div style={{ maxWidth: 640, margin: "0 auto 56px", textAlign: "center" }}>
							<div style={eyebrow}>What We Offer</div>
							<h2 style={sectionHeading}>Our Services</h2>
							<p style={{ fontFamily: "var(--body-font)", fontSize: 16, lineHeight: 1.6, color: "var(--text-muted)", margin: 0 }}>
								A wide range of consultation, diagnostic and therapeutic services, discussed in more detail at the
								time of your consultation.
							</p>
						</div>
						<div className="services-grid" style={{ maxWidth: 1344, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 24 }}>
							<ServiceCard
								bg="var(--primary)"
								title="Gastroenterology & Hepatology"
								text="Consultation, diagnosis and treatment across the full range of digestive and liver conditions."
								icon={
									<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6">
										<path d="M8 3c0 2-1.5 3-1.5 5.5S8 13 12 13s5.5-2 5.5-4.5S16 5 16 3" />
										<path d="M6 13c-1.5 1.5-2 3-2 5a8 8 0 0 0 16 0c0-2-.5-3.5-2-5" />
									</svg>
								}
							/>
							<ServiceCard
								bg="var(--warm)"
								title="Endoscopy & Interventional Procedures"
								text="Gastroscopy, colonoscopy, ERCP and advanced endoscopic techniques performed at Hollywood Private Hospital."
								icon={
									<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6">
										<circle cx="12" cy="12" r="4.5" />
										<circle cx="12" cy="12" r="1.4" fill="#fff" stroke="none" />
										<path d="M12 3v2.2M12 18.8V21M3 12h2.2M18.8 12H21" />
									</svg>
								}
							/>
							<ServiceCard
								bg="var(--primary-dark)"
								title="Rheumatology"
								text="Consultation, diagnosis and ongoing management of routine rheumatological conditions."
								icon={
									<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6">
										<rect x="4" y="10" width="7" height="10" rx="2" />
										<rect x="13" y="4" width="7" height="16" rx="2" />
										<path d="M4 15h7M13 12h7" />
									</svg>
								}
							/>
							<ServiceCard
								bg="var(--warm-dark)"
								title="Diagnostic Testing"
								text="A full range of diagnostic investigations to support accurate, timely diagnosis."
								icon={
									<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6">
										<path d="M9 2v6.5L4.5 17a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L15 8.5V2" />
										<path d="M9 2h6M8 13h8" />
									</svg>
								}
							/>
						</div>
					</section>

					{/* Testimonials */}
					{listOfTestimonials.length > 0 ? (
						<section className="section-pad" style={{ ...fullBleed, background: "var(--tint-blue)", padding: "96px 48px" }}>
							<div style={{ maxWidth: 640, margin: "0 auto 56px", textAlign: "center" }}>
								<div style={eyebrow}>Testimonials</div>
								<h2 style={{ ...sectionHeading, marginBottom: 0 }}>What Our Patients Say</h2>
							</div>
							<div className="testimonial-row" style={{ maxWidth: 1000, margin: "0 auto", display: "flex", gap: 28 }}>
								{listOfTestimonials.slice(0, noOfPreviewTestimonials).map((testimonial, i) => (
									<div
										key={testimonial.text}
										style={{ flex: 1, background: "var(--surface)", borderRadius: 14, padding: 32, border: "1px solid var(--border)" }}
									>
										<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
											<svg width="40" height="40" viewBox="0 0 40 40">
												<rect x="0" y="0" width="40" height="40" rx="10" fill={i % 2 === 0 ? "var(--warm)" : "var(--primary)"} />
												<path
													d="M13 24V19c0-3.3 2-5.4 5-6v2.4c-1.6.5-2.5 1.7-2.5 3.6h2.5V24h-5zm9 0V19c0-3.3 2-5.4 5-6v2.4c-1.6.5-2.5 1.7-2.5 3.6H27V24h-5z"
													fill="#fff"
												/>
											</svg>
											<p style={{ fontFamily: "var(--body-font)", fontSize: 13, color: "var(--text-muted)", margin: 0 }}>Advanced GI WA patient</p>
										</div>
										<p style={{ fontFamily: "var(--heading-font)", fontSize: 16.5, lineHeight: 1.6, color: "var(--text)", margin: 0, fontWeight: 600 }}>
											&ldquo;{testimonial.text}&rdquo;
										</p>
									</div>
								))}
							</div>
							<div style={{ textAlign: "center", marginTop: 32 }}>
								<Link to="/testimonials" className="btn-primary" style={{ ...btnStyle, border: "none" }}>
									See all testimonials
								</Link>
							</div>
						</section>
					) : null}

					{/* Closing CTA */}
					<section style={{ ...fullBleed, position: "relative", background: "var(--primary-dark)", padding: "80px 48px", textAlign: "center", overflow: "hidden" }}>
						<svg style={{ position: "absolute", top: -60, left: -40 }} width="220" height="220" viewBox="0 0 220 220">
							<circle cx="110" cy="110" r="110" fill="var(--warm)" opacity="0.18" />
						</svg>
						<svg style={{ position: "absolute", bottom: -70, right: -30 }} width="260" height="260" viewBox="0 0 260 260">
							<circle cx="130" cy="130" r="130" fill="#ffffff" opacity="0.06" />
						</svg>
						<div style={{ position: "relative" }}>
							<h2 style={{ fontFamily: "var(--heading-font)", fontWeight: 800, fontSize: 28, color: "#fff", margin: "0 0 14px" }}>
								Ready to arrange a consultation?
							</h2>
							<p style={{ fontFamily: "var(--body-font)", fontSize: 16, color: "rgba(255,255,255,0.85)", margin: "0 0 30px" }}>
								Contact our team to book an appointment or request more information.
							</p>
							<Link to="/contact" className="btn-warm" style={{ ...btnStyle, padding: "13px 30px" }}>
								Contact Us
							</Link>
						</div>
					</section>
				</div>
			)}
		/>
	);
}

export default IndexPage;
